import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api';

type All = {
  portal: { name: string; announcement: string | null; support: { email: string | null } };
  security: { minPasswordLength: number; maxFailedLogins: number; lockoutMinutes: number };
  users: { defaultUserTypeId: number | null; defaultTimezone: string; defaultLocale: string };
};
type Payload = { settings: All; userTypes: { id: number; name: string }[]; passwordFloor: number };

/**
 * Portal settings.
 *
 * Only settings that genuinely take effect are here. Anything Learnica does
 * not honour yet is not offered, because a switch that changes nothing is
 * worse than no switch — somebody tightens the lockout, believes they have,
 * and finds out otherwise after an incident.
 */
export function Settings() {
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<Payload>('/settings')); }
    catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const { settings, userTypes, passwordFloor } = data;

  const save = async (part: Partial<All>, said: string) => {
    setBusy(true); setError(null);
    try {
      const res = await api.patch<{ settings: All }>('/settings', part);
      setData({ ...data, settings: res.settings });
      setNote(said);
    } catch (e) { setError((e as ApiError).message); setNote(null); }
    finally { setBusy(false); }
  };

  return (
    <>
      <h1 className="h1">Portal settings</h1>
      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <PortalPanel settings={settings} busy={busy} onSave={save} />
      <SecurityPanel settings={settings} floor={passwordFloor} busy={busy} onSave={save} />
      <UsersPanel settings={settings} userTypes={userTypes} busy={busy} onSave={save} />
      <MailPanel />
    </>
  );
}

function PortalPanel({ settings, busy, onSave }: {
  settings: All; busy: boolean;
  onSave: (part: Partial<All>, said: string) => Promise<void>;
}) {
  const [form, setForm] = useState({
    name: settings.portal.name,
    announcement: settings.portal.announcement ?? '',
    supportEmail: settings.portal.support.email ?? '',
  });

  return (
    <section className="panel">
      <h2 className="h2">The portal</h2>
      <div className="form">
        <label><span>Name</span>
          <input value={form.name} maxLength={100}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <em className="muted small">Shown in the top bar, on the sign-in page and on certificates.</em>
        </label>

        <label><span>Announcement</span>
          <textarea rows={2} maxLength={500} value={form.announcement}
            placeholder="Shown to everybody until you clear it"
            onChange={(e) => setForm({ ...form, announcement: e.target.value })} />
        </label>

        <label><span>Support email</span>
          <input type="email" value={form.supportEmail}
            onChange={(e) => setForm({ ...form, supportEmail: e.target.value })} />
        </label>

        <button className="btn btn--primary" disabled={busy} onClick={() => void onSave({
          portal: {
            name: form.name.trim(),
            announcement: form.announcement.trim() || null,
            support: { email: form.supportEmail.trim() || null },
          },
        } as Partial<All>, 'Saved.')}>Save</button>
      </div>
    </section>
  );
}

function SecurityPanel({ settings, floor, busy, onSave }: {
  settings: All; floor: number; busy: boolean;
  onSave: (part: Partial<All>, said: string) => Promise<void>;
}) {
  const [form, setForm] = useState({
    minPasswordLength: String(settings.security.minPasswordLength),
    maxFailedLogins: String(settings.security.maxFailedLogins),
    lockoutMinutes: String(settings.security.lockoutMinutes),
  });

  return (
    <section className="panel">
      <h2 className="h2">Signing in</h2>
      <p className="muted small">
        These take effect straight away — the next password change and the next wrong password.
      </p>
      <div className="form">
        <label><span>Shortest password</span>
          <span className="withhint">
            <input type="number" className="narrow" min={floor} max={64} value={form.minPasswordLength}
              onChange={(e) => setForm({ ...form, minPasswordLength: e.target.value })} />
            <em>characters — never fewer than {floor}, whatever is set</em>
          </span>
        </label>

        <label><span>Lock an account after</span>
          <span className="withhint">
            <input type="number" className="narrow" min={3} max={50} value={form.maxFailedLogins}
              onChange={(e) => setForm({ ...form, maxFailedLogins: e.target.value })} />
            <em>wrong passwords in a row</em>
          </span>
        </label>

        <label><span>Locked for</span>
          <span className="withhint">
            <input type="number" className="narrow" min={1} max={1440} value={form.lockoutMinutes}
              onChange={(e) => setForm({ ...form, lockoutMinutes: e.target.value })} />
            <em>minutes</em>
          </span>
        </label>

        <button className="btn btn--primary" disabled={busy} onClick={() => void onSave({
          security: {
            minPasswordLength: Number(form.minPasswordLength),
            maxFailedLogins: Number(form.maxFailedLogins),
            lockoutMinutes: Number(form.lockoutMinutes),
          },
        } as Partial<All>, 'Saved. This applies from the next sign-in.')}>Save</button>
      </div>
    </section>
  );
}

function UsersPanel({ settings, userTypes, busy, onSave }: {
  settings: All; userTypes: { id: number; name: string }[]; busy: boolean;
  onSave: (part: Partial<All>, said: string) => Promise<void>;
}) {
  const [form, setForm] = useState({
    defaultUserTypeId: settings.users.defaultUserTypeId == null ? '' : String(settings.users.defaultUserTypeId),
    defaultTimezone: settings.users.defaultTimezone,
    defaultLocale: settings.users.defaultLocale,
  });

  return (
    <section className="panel">
      <h2 className="h2">New accounts</h2>
      <div className="form">
        <label><span>Start as</span>
          <select value={form.defaultUserTypeId}
            onChange={(e) => setForm({ ...form, defaultUserTypeId: e.target.value })}>
            <option value="">Learnica decides</option>
            {userTypes.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
          <em className="muted small">
            You can only choose a type that can do no more than you can.
          </em>
        </label>

        <label><span>Time zone</span>
          <input value={form.defaultTimezone}
            onChange={(e) => setForm({ ...form, defaultTimezone: e.target.value })} />
        </label>

        <label><span>Language</span>
          <select value={form.defaultLocale}
            onChange={(e) => setForm({ ...form, defaultLocale: e.target.value })}>
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="id">Bahasa Indonesia</option>
          </select>
        </label>

        <button className="btn btn--primary" disabled={busy} onClick={() => void onSave({
          users: {
            defaultUserTypeId: form.defaultUserTypeId ? Number(form.defaultUserTypeId) : null,
            defaultTimezone: form.defaultTimezone.trim(),
            defaultLocale: form.defaultLocale,
          },
        } as Partial<All>, 'Saved.')}>Save</button>
      </div>
    </section>
  );
}


type MailStatus = {
  driver: string; from: string; sends: boolean; yourEmail: string | null;
  queued: number; sent: number; failed: number; lastError: string | null;
};

/**
 * Can this portal send email?
 *
 * Here because it is the question somebody asks when a trainee says they
 * never got their reset link, and because "it is configured" and "a message
 * arrived" are different answers — only the second is worth anything, and
 * only a person looking in an inbox can give it.
 */
function MailPanel() {
  const [status, setStatus] = useState<MailStatus | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setStatus(await api.get<MailStatus>('/admin/mail')); } catch { /* shown below */ }
    })();
  }, []);

  if (!status) return null;

  return (
    <section className="panel">
      <h2 className="h2">Email</h2>

      {!status.sends ? (
        <p className="notice notice--warn">
          <strong>This portal cannot send email.</strong> Messages are being written to the server
          log and nothing is leaving. Nobody who forgets their password can be sent a link, so
          this has to be set up before anyone relies on it.
        </p>
      ) : (
        <p className="muted small">
          Sending through <strong>{status.driver}</strong> as <code>{status.from}</code>.
        </p>
      )}

      <dl className="facts facts--wide">
        <div><dt>Waiting</dt><dd>{status.queued}</dd></div>
        <div><dt>Sent</dt><dd>{status.sent}</dd></div>
        <div><dt>Failed</dt><dd className={status.failed ? 'warn' : undefined}>{status.failed}</dd></div>
      </dl>
      {status.lastError && (
        <p className="warn small">Last failure: {status.lastError}</p>
      )}

      <p className="row">
        <button type="button" disabled={busy || !status.yourEmail} onClick={async () => {
          setBusy(true); setResult(null);
          try {
            const res = await api.post<{ message: string }>('/admin/mail/test');
            setResult({ ok: true, text: res.message });
          } catch (e) {
            setResult({ ok: false, text: (e as ApiError).message });
          } finally { setBusy(false); }
        }}>
          {busy ? 'Sending…' : 'Send me a test email'}
        </button>
        <span className="muted small">
          {status.yourEmail
            ? `Goes to ${status.yourEmail} — your own address, and nowhere else.`
            : 'Your account has no email address, so there is nowhere to send one.'}
        </span>
      </p>

      {result && (
        <p className={result.ok ? 'notice' : 'auth__error'}>{result.text}</p>
      )}
    </section>
  );
}
